import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  DollarSign, 
  TrendingUp, 
  CreditCard,
  Calendar
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#0EA5E9', '#8B5CF6', '#10B981', '#F59E0B'];

export default function AdminRevenue() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRevenue();
  }, []);

  const fetchRevenue = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/revenue`, {
        headers: getAuthHeader()
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch revenue:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)}L`;
    return `₹${amount.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-navy-200 rounded w-1/4" />
          <div className="grid grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-32 bg-navy-200 rounded-xl" />
            ))}
          </div>
          <div className="h-80 bg-navy-200 rounded-xl" />
        </div>
      </div>
    );
  }

  const revenueByPlanData = Object.entries(data?.revenue_by_plan || {}).map(([plan, amount]) => ({
    name: plan.charAt(0).toUpperCase() + plan.slice(1),
    value: amount
  }));

  return (
    <div className="p-6 lg:p-8" data-testid="admin-revenue">
      <div className="mb-8">
        <h1 className="text-2xl font-heading font-bold text-navy-900">Revenue Dashboard</h1>
        <p className="text-navy-600">Track your platform earnings</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-navy-600 mb-1">Total Revenue</p>
                <p className="text-3xl font-bold text-navy-900">
                  {formatCurrency(data?.total_revenue || 0)}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-navy-600 mb-1">This Month</p>
                <p className="text-3xl font-bold text-navy-900">
                  {formatCurrency(data?.monthly_revenue || 0)}
                </p>
              </div>
              <div className="w-12 h-12 bg-sky-500 rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-navy-600 mb-1">Active Subscriptions</p>
                <p className="text-3xl font-bold text-navy-900">
                  {(data?.active_subscriptions?.basic || 0) + (data?.active_subscriptions?.premium || 0)}
                </p>
                <p className="text-xs text-navy-500 mt-1">
                  Basic: {data?.active_subscriptions?.basic || 0} | Premium: {data?.active_subscriptions?.premium || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* Revenue by Plan */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Revenue by Plan</CardTitle>
          </CardHeader>
          <CardContent>
            {revenueByPlanData.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-navy-500">
                No revenue data yet
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={revenueByPlanData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${formatCurrency(value)}`}
                    >
                      {revenueByPlanData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Subscription Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Subscription Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[
                  { name: 'Basic', count: data?.active_subscriptions?.basic || 0 },
                  { name: 'Premium', count: data?.active_subscriptions?.premium || 0 }
                ]}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#0EA5E9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Transactions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-sky-500" />
            Recent Transactions
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data?.recent_transactions?.length === 0 ? (
            <p className="text-navy-500 text-center py-8">No transactions yet</p>
          ) : (
            <div className="space-y-3">
              {data?.recent_transactions?.map((transaction, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-navy-50 rounded-lg">
                  <div>
                    <p className="font-medium text-navy-900">
                      {transaction.type === 'subscription' ? `${transaction.plan_id} Subscription` : 'Property Boost'}
                    </p>
                    <p className="text-sm text-navy-500">
                      {new Date(transaction.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-navy-900">{formatCurrency(transaction.amount)}</p>
                    <p className={`text-xs ${transaction.payment_status === 'paid' ? 'text-green-600' : 'text-orange-600'}`}>
                      {transaction.payment_status}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
